import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Trash2, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CONNECTOR_META } from "./shared";

export default function TemplateModal({ onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const [requestor, setRequestor] = useState("");
  const [slaHours, setSlaHours] = useState(24);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orchestration/templates").then(({ data }) => {
    setTemplates(data); if (data[0]) setTid((cur) => cur || data[0].id);
  }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const selected = templates.find((t) => t.id === tid);

  const submit = async () => {
    if (!tid) { toast.error("Pick a role template."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orchestration/jobs/from-template", { template_id: tid, requestor, sla_hours: Number(slaHours) || 24 });
      toast.success(`Job created from template with ${data.task_total} task(s).`);
      onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const delTemplate = async (id) => {
    try { await api.delete(`/orchestration/templates/${id}`); toast.success("Template deleted."); if (tid === id) setTid(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="from-template-modal">
      <div className="bg-[var(--parchment)] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="brand-band text-white px-6 py-4 flex items-center justify-between sticky top-0">
          <p className="font-semibold">Create job from a role template</p>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-500">Role templates enforce least-privilege by predefining exactly which apps, entitlements and approval/MFA gates a role should get.</p>
          <div>
            <label className="text-xs font-semibold text-neutral-600">Role template</label>
            <select data-testid="template-select" value={tid} onChange={(e) => setTid(e.target.value)} className="field-input mt-1">
              {!templates.length && <option value="">No templates</option>}
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.tasks.length} app(s)</option>)}
            </select>
          </div>

          {selected && (
            <div className="card-surface p-3 text-xs space-y-1" data-testid="template-preview">
              {selected.description && <p className="text-neutral-500 mb-1">{selected.description}</p>}
              {selected.tasks.map((t, i) => (
                <div key={i} className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-neutral-700">{t.app_name}</span>
                  <span className={`px-1.5 py-0.5 rounded-full ${CONNECTOR_META[t.connector]?.cls}`}>{CONNECTOR_META[t.connector]?.label}</span>
                  <span className="text-neutral-400">{t.action} → {t.target || "—"}</span>
                  {t.requires_approval && <span className="text-indigo-600">· approval</span>}
                  {(t.requires_mfa || t.connector === "rpa") && <span className="text-orange-600">· MFA</span>}
                </div>
              ))}
              <button onClick={() => delTemplate(selected.id)} className="text-red-500 hover:text-red-700 text-[11px] mt-1 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete template</button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-600">Requestor</label>
              <input data-testid="template-requestor-input" value={requestor} onChange={(e) => setRequestor(e.target.value)} placeholder="user@wemabank.com" className="field-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-600">SLA (hours)</label>
              <input type="number" min={1} value={slaHours} onChange={(e) => setSlaHours(e.target.value)} className="field-input mt-1" />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn btn-outline">Cancel</button>
            <button data-testid="from-template-submit" onClick={submit} disabled={busy || !tid} className="btn btn-primary">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create job"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
